import React from 'react';
import { useSelector } from 'react-redux';

import type { IconProps } from '@travel/icons';
import {
  CovidBath,
  CovidCheckinAndOut,
  CovidEmergency,
  CovidMeal,
  CovidRequestGuest,
  CovidRoom,
  CovidStaff,
  CovidSterilization,
  CovidVentilation,
} from '@travel/icons/service';
import { ArrowRight, TranslateBadge } from '@travel/icons/ui';
import { MOBILE_SCREEN, useDeviceType, useDialogHandler } from '@travel/traveler-core/hooks';
import { IconTextLink } from '@travel/ui';
import { cx, isEmptyArray } from '@travel/utils';

import Dialog from 'components/Dialog';

import { getItem } from 'store/providerInformation/selectors';

import { Translate } from 'core/translate';
import type { CovidInfoItem } from 'ProviderInformation-Types';

import styles from './basicInformation.module.scss';

const COVID_ICONS: { [key: string]: React.ComponentType<React.PropsWithChildren<IconProps>> } = {
  COVID19_Staff: (props) => <CovidStaff {...props} />,
  COVID19_Sterilization: (props) => <CovidSterilization {...props} />,
  COVID19_Ventilation: (props) => <CovidVentilation {...props} />,
  COVID19_Check_In_and_Out: (props) => <CovidCheckinAndOut {...props} />,
  COVID19_Room: (props) => <CovidRoom {...props} />,
  COVID19_Meal: (props) => <CovidMeal {...props} />,
  COVID19_Request_to_Guest: (props) => <CovidRequestGuest {...props} />,
  COVID19_Emergency: (props) => <CovidEmergency {...props} />,
  COVID19_Bathtub: (props) => <CovidBath {...props} />,
};
const COVIDRemarks = 'COVID19_Remarks';

type FeatureItemProps = {
  className?: string;
  /** Flag to define whether the features should be displayed or not */
  isMobilePreviewMode?: boolean;
} & CovidInfoItem;

function FeatureItem(props: FeatureItemProps) {
  const Icon = COVID_ICONS[props.management.trim()];

  return (
    <div
      className={cx(
        styles.featureItem,
        props.isMobilePreviewMode && styles.preview,
        props.className,
      )}
    >
      {Icon && (
        <Icon className={styles.covidFeatureIcon} size={props.isMobilePreviewMode ? 24 : 32} />
      )}
      <div className={styles.featureInfo}>
        <h4 className={styles.featureTitle}>{props.group}</h4>
        {!props.isMobilePreviewMode && (
          <ul className={styles.covidSubFeatureList}>
            {props.features?.map((feature) => <li key={feature.id}>{feature.title}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

function CovidInformation(props: { className?: string }) {
  const { isOpen, onToggle } = useDialogHandler(false);
  const provider = useSelector(getItem);
  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const displayCount = isMobile ? 4 : undefined;

  if (isEmptyArray(provider.covid)) {
    return null;
  }

  return (
    <div
      className={cx(styles.covidInfoWrapper, props.className)}
      data-testid="covidInformation-wrapper"
    >
      <h3 className={styles.subTitle}>
        <Translate id="Provider_Info.Basic_Info.COVID_Title" />
      </h3>

      <div className={styles.featureList}>
        {provider.covid
          ?.slice(0, displayCount)
          .map((item) => (
            <FeatureItem
              key={item.management}
              {...item}
              isMobilePreviewMode={isMobile}
              className={
                item.management !== COVIDRemarks || isMobile
                  ? styles.featureSize
                  : styles.featureSizeFull
              }
            />
          ))}
      </div>

      {provider.isCovidTextGoogleTranslated && !isMobile && <TranslateBadge />}
      {isMobile && (
        <div className={styles.seeMoreLink}>
          <IconTextLink
            onClick={onToggle}
            text={<Translate id="Provider_Info.Tablet_PC.See_More" />}
            icon={<ArrowRight color="cilantro" size={16} />}
            iconPosition="right"
          />
        </div>
      )}

      {isMobile && (
        <Dialog
          isOpen={isOpen}
          title={<Translate id="Provider_Info.Basic_Info.COVID_Title" />}
          onClose={onToggle}
          isUseContentFlexBox={false}
          isSEOContentIndexedOnHidden={true}
          hasAnimation={true}
        >
          <div
            className={styles.covidDialog}
            data-testid="providerInfo-covidInformation-dialogContent"
          >
            {provider.covid?.map((item) => <FeatureItem key={item.management} {...item} />)}
            {provider.isCovidTextGoogleTranslated && (
              <div className={styles.featureItem}>
                <TranslateBadge data-testid="covidInformation-dialog-translateBadge" />
              </div>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}

export default CovidInformation;
